import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

type Mode = 'signin' | 'forgot-step1' | 'forgot-step2';

const Login: React.FC = () => {
  const { login, forgotPassword, confirmNewPassword, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/runs', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#EF8997' }} />
      </Box>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 0',
    fontSize: '16px',
    color: '#fff',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/runs', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setMode('forgot-step2');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmNewPassword(email, code, newPassword);
      setResetSuccess(true);
      setTimeout(() => {
        setMode('signin');
        setResetSuccess(false);
        setCode('');
        setNewPassword('');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn 300ms ease-in',
        '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          bgcolor: '#0a0a0a',
          border: '1px solid rgba(255,255,255,0.12)',
          p: 4,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img src="/exo-logo.png" alt="Exo" style={{ height: 40, marginBottom: 12 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Canard
          </Typography>
        </Box>

        {mode === 'signin' && (
          <form onSubmit={handleSignIn}>
            <Box sx={{ mb: 2.5 }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                disabled={loading}
                required
                style={inputStyle}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                style={inputStyle}
              />
            </Box>
            {error && (
              <Typography sx={{ color: '#ff6b6b', fontSize: 14, mb: 2 }}>{error}</Typography>
            )}
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                bgcolor: '#EF8997',
                color: '#000',
                height: 48,
                fontWeight: 600,
                fontSize: 16,
                '&:hover': { bgcolor: '#e07080' },
                '&:disabled': { bgcolor: '#EF8997', opacity: 0.7 },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Sign in'}
            </Button>
            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
              <Typography
                component="span"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 14,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => { setMode('forgot-step1'); setError(''); }}
              >
                Forgot password?
              </Typography>
            </Box>
            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
              <Typography
                component="span"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 14,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => navigate('/signup')}
              >
                Don't have an account? Sign up
              </Typography>
            </Box>
          </form>
        )}

        {mode === 'forgot-step1' && (
          <form onSubmit={handleForgotSubmit}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Reset password
            </Typography>
            <Box sx={{ mb: 3 }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                disabled={loading}
                required
                style={inputStyle}
              />
            </Box>
            {error && (
              <Typography sx={{ color: '#ff6b6b', fontSize: 14, mb: 2 }}>{error}</Typography>
            )}
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                bgcolor: '#EF8997',
                color: '#000',
                height: 48,
                fontWeight: 600,
                fontSize: 16,
                '&:hover': { bgcolor: '#e07080' },
                '&:disabled': { bgcolor: '#EF8997', opacity: 0.7 },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Send reset code'}
            </Button>
            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
              <Typography
                component="span"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 14,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => { setMode('signin'); setError(''); }}
              >
                Back to sign in
              </Typography>
            </Box>
          </form>
        )}

        {mode === 'forgot-step2' && (
          <form onSubmit={handleResetPassword}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Enter reset code
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, mb: 2.5 }}>
              We sent a code to {email}
            </Typography>
            <Box sx={{ mb: 2.5 }}>
              <input
                type="text"
                placeholder="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                disabled={loading}
                required
                style={inputStyle}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                required
                style={inputStyle}
              />
            </Box>
            {error && (
              <Typography sx={{ color: '#ff6b6b', fontSize: 14, mb: 2 }}>{error}</Typography>
            )}
            {resetSuccess && (
              <Typography sx={{ color: '#51cf66', fontSize: 14, mb: 2 }}>
                Password reset successful
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                bgcolor: '#EF8997',
                color: '#000',
                height: 48,
                fontWeight: 600,
                fontSize: 16,
                '&:hover': { bgcolor: '#e07080' },
                '&:disabled': { bgcolor: '#EF8997', opacity: 0.7 },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Reset password'}
            </Button>
            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
              <Typography
                component="span"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 14,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => { setMode('signin'); setError(''); }}
              >
                Back to sign in
              </Typography>
            </Box>
          </form>
        )}
      </Box>
    </Box>
  );
};

export default Login;
